import { useAppNavigate } from "../../../hooks/useAppNavigate";
import React, { FC, useEffect, useMemo, useRef, useState } from "react";
import {
    Backdrop,
    Box,
    Button,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Fade,
    Grid,
    Modal,
    Tab,
    Tabs,
    Typography,
} from "@mui/material";
import { useStory } from "../../../hooks/useStory";
import { FacilitatorStoryPartType, SessionType, WeekType } from "../../../models/modelTypes";
import { useParams } from "react-router-dom";
import { useFetch } from "../../../core/components/crud/contexts/fetch.context";
import { Breadcrumb } from "../../../core/components/Breadcrumb";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import Spinner from "../../layout/Spinner";
import { Error } from "../../../core/components/Error";
import { verbalTheme } from "../../layout/themes/verbal.theme";
import { publicTheme } from "../../layout/themes/public.theme";
import { TabPanel } from "../../programme/screens/ViewProgramme";
import { useMobile } from "../../../hooks/useMobile";
import { VTooltip } from "../../../core/components/VTooltip";
import { StorySynopsis } from "../components/StorySynopsis";
import { FileViewer } from "../../common/FileViewer";
import { useAuth } from "../../../contexts/auth.context";
import { useSetTourState, useTrackedTourState } from "../../tour/Store";
import { useBranchedStory } from "../../curation/branched-stories/hooks/useBranchedStory";
import { BranchedStoryAnnotationView } from "../../curation/branched-stories/components/BranchedStoryAnnotationView";
import { extractGDFolderId } from "../../../utils/googleDrive";
import { useTimedVisibility } from "../../../hooks/useTimedVisibility";
import { StoryPartAnnotation } from "./StoryPartAnnotation";

type PrepareStoryProps = {
    classroomId?: number;
};

/**
 * Shared Action Footer for Save and Cancel buttons.
 */
const ActionFooter: FC<{
    onCancel: () => void;
    onSave: () => void;
    isSaving: boolean;
    dataTour?: string;
}> = ({ onCancel, onSave, isSaving, dataTour }) => (
    <Box mb={2} data-tour={dataTour}>
        <Grid container justifyContent="space-between">
            <Grid item>
                <Button variant="contained" size="large" onClick={onCancel} style={{ fontWeight: "bold" }}>
                    Cancel
                </Button>
            </Grid>
            <Grid item>
                <Button
                    variant="contained"
                    size="large"
                    color="secondary"
                    disabled={isSaving}
                    onClick={onSave}
                    style={{ fontWeight: "bold" }}
                >
                    {isSaving ? "Saving..." : "Save Annotations"}
                </Button>
            </Grid>
        </Grid>
    </Box>
);

export const PrepareStory: React.FC<PrepareStoryProps> = (props: PrepareStoryProps): JSX.Element => {
    const { classroomId } = props;
    const { item, fetchItem } = useFetch<SessionType>();
    const { isMobile, isTablet } = useMobile();
    const { user } = useAuth();
    const { facilitatorStoryParts, getFacilitatorStoryParts, addFacilitatorStoryPart } = useStory();
    const setTourState = useSetTourState();
    const tourState = useTrackedTourState();

    const [content, setContent] = useState<Map<number, string>>(new Map());
    const [saving, setSaving] = useState<boolean>(false);
    const [isEdited, setIsEdited] = useState<boolean>(false);
    const [confirmCancel, setConfirmCancel] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [loadingImagePreviews, setLoadingImagePreviews] = useState<boolean[]>();
    const [error, setError] = useState<boolean>(false);
    const [isEditorLoaded, setIsEditorLoaded] = useState<boolean>(false);
    const [isQuestionsOpen, setIsQuestionsOpen] = useState<Map<number, boolean>>(new Map());
    const [week, setWeek] = useState<WeekType>();
    const [driveFolderId, setDriveFolderId] = useState<string>();
    const [accordionOpen, setAccordionOpen] = useState<Map<number, boolean>>(new Map());

    const [tooltipOpen, setTooltipOpen] = useTimedVisibility(false, 5000);
    const [storyTooltipOpen, setStoryTooltipOpen] = useTimedVisibility(false, 5000);

    // Tabs
    const [tabValue, setTabValue] = useState<number>(0);
    const partRefs = useRef<HTMLDivElement[]>([]);
    const refStoryTab = useRef<HTMLDivElement | null>(null);
    const refSynopsisTab = useRef<HTMLDivElement | null>(null);
    const refResourcesTab = useRef<HTMLDivElement | null>(null);
    const refShowQuestions = useRef<HTMLButtonElement | null>(null);
    const refHideQuestions = useRef<HTMLButtonElement | null>(null);

    const { id } = useParams<{ id?: string }>();
    const { push } = useAppNavigate();
    const [openModalImagePreview, setOpenModalImagePreview] = useState(false);
    const [modalImage, setModalImage] = useState("false");

    // Branched story detection and data loading
    const isBranchedStory = item?.MappedStory?.Story?.type === "BranchedStory";
    const storyId = item?.MappedStory?.Story?.id;
    const {
        structure: branchedStructure,
        loading: branchedLoading,
        error: branchedError,
    } = useBranchedStory(isBranchedStory ? storyId : undefined);

    const programmeUrl = useMemo(() => {
        return classroomId
            ? `/classrooms/view/${classroomId}/view/${item?.programmeId}`
            : `/programmes/view/${item?.programmeId}`;
    }, [classroomId, item?.programmeId]);

    useEffect(() => {
        setTourState((prev) => ({
            ...prev,
            refs: prev.refs
                .set("prepareStoryTab", refStoryTab)
                .set("prepareSynopsisTab", refSynopsisTab)
                .set("prepareResourcesTab", refResourcesTab)
                .set("prepareShowQuestions", refShowQuestions)
                .set("prepareHideQuestions", refHideQuestions),
        }));
    }, [refStoryTab, refSynopsisTab, refResourcesTab, refShowQuestions, refHideQuestions]);

    useEffect(() => {
        if (id) {
            setLoading(true);
            fetchItem(parseInt(id))
                .then(() => setError(false))
                .catch(() => setError(true))
                .finally(() => setLoading(false));
        } else {
            setError(true);
        }
    }, [id]);

    useEffect(() => {
        if (item && item.MappedStory && item.MappedStory.storyId) {
            getFacilitatorStoryParts(item.MappedStory.storyId);
        }
        if (
            item &&
            item.MappedStory &&
            item.MappedStory.StoryCollection &&
            item.MappedStory.StoryCollection.Weeks &&
            item.MappedStory.StoryCollection.Weeks.length
        ) {
            const currentWeek = item.MappedStory.StoryCollection.Weeks.find(
                (week) => week.order === item.MappedStory?.order,
            );
            if (currentWeek) setWeek(currentWeek);
        }
    }, [item]);

    useEffect(() => {
        const resourcesUrl =
            user?.Organisation?.country === "Republic of Ireland"
                ? item?.MappedStory?.Story?.resourcesROI
                : item?.MappedStory?.Story?.resources;

        if (resourcesUrl) {
            setDriveFolderId(extractGDFolderId(resourcesUrl));
        }
    }, [item?.MappedStory?.Story, user?.Organisation?.country]);

    // Needed as TinyMCE editor isn't activating if only one part in story
    useEffect(() => {
        if (facilitatorStoryParts && facilitatorStoryParts.length === 1) {
            setIsEditorLoaded(true);
        }
    }, [facilitatorStoryParts]);

    useEffect(() => {
        if (facilitatorStoryParts && facilitatorStoryParts.length > 0) {
            const nextQuestionsOpen = new Map(isQuestionsOpen);
            facilitatorStoryParts.forEach((part) => nextQuestionsOpen.set(part.id, true));
            setIsQuestionsOpen(nextQuestionsOpen);

            setLoadingImagePreviews(new Array(facilitatorStoryParts.length).fill(true));
        }
    }, [facilitatorStoryParts]);

    // Open tooltips temporarily when editor is loaded/item is loaded
    useEffect(() => {
        if (isEditorLoaded && !tourState.openTour) {
            setTooltipOpen(true);
        }
    }, [isEditorLoaded, tourState.openTour]);

    useEffect(() => {
        if (item && !loading && !tourState.openTour) {
            setStoryTooltipOpen(true);
        }
    }, [item, loading, tourState.openTour]);

    useEffect(() => {
        if (tabValue !== 0) {
            setStoryTooltipOpen(false);
        }
    }, [tabValue]);

    const handleEditorChange = (id: number, newContent: string) => {
        setContent(new Map(content?.set(id, newContent)));
    };

    const handleSaveAnnotation = async () => {
        if (content) {
            setSaving(true);
            try {
                const savePromises = Array.from(content.entries()).map(([key, value]) => {
                    const facilitatorStory = {
                        html: value,
                        mappedStoryPartId: key,
                    } as Partial<FacilitatorStoryPartType>;
                    return addFacilitatorStoryPart(facilitatorStory);
                });

                await Promise.all(savePromises);
                setSaving(false);
                push(programmeUrl);
            } catch (error) {
                setSaving(false);
                console.error("Error saving annotations:", error);
            }
        }
    };

    const handleCancel = () => {
        if (isEdited) {
            setConfirmCancel(true);
        } else {
            push(programmeUrl);
        }
    };

    const handleLoadingImagePreviews = (index: number, value: boolean) => {
        if (loadingImagePreviews) {
            const newLoadingImagePreview = [...loadingImagePreviews];
            newLoadingImagePreview[index] = value;
            setLoadingImagePreviews(newLoadingImagePreview);
        }
    };

    const handleModalImagePreviewClose = () => {
        setOpenModalImagePreview(false);
    };

    const handleModalImage = (value: string) => {
        setModalImage(value);
        setOpenModalImagePreview(true);
    };

    const useStyles = makeStyles(
        createStyles({
            gridRowItem: {
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
            },
            prepareLayout: {
                display: "grid",
                gridTemplateColumns: "auto",
            },
            pill: {
                backgroundColor: publicTheme.palette.themePill.main,
                color: verbalTheme.palette.common.white,
            },
            content: {
                padding: 0,
                margin: 0,
            },
            resourceLink: {
                color: verbalTheme.palette.primary.main,
                textDecoration: "none",
            },
            modal: {
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
            },
            fadeImg: {
                outline: "none",
            },
        }),
    );

    const classes = useStyles();

    if (loading) {
        return <Spinner />;
    }

    if (error) {
        return <Error description={`Unable to load session`} />;
    }

    return (
        <div className={classes.prepareLayout}>
            {!isTablet && isMobile && <div style={{ marginTop: 56 }} />}
            <Box pt={!isTablet && isMobile ? 5 : 4} p={isMobile ? 0 : 4}>
                <Container maxWidth={"xl"} style={isMobile ? { paddingLeft: 0, paddingRight: 0 } : undefined}>
                    <Box data-tour="prep-session-bc" mt={4} mb={2} pl={6}>
                        <Breadcrumb crumbs={[{ name: "Programme", url: programmeUrl }]} current={"Annotate Story"} />
                    </Box>
                    <Box mb={2} pl={6}>
                        <Typography data-tour="prep-story-name" variant="h1">
                            {item?.MappedStory?.Story?.storyName}
                        </Typography>
                    </Box>
                    <Box pl={6}>
                        <Tabs
                            textColor="primary"
                            indicatorColor="secondary"
                            value={tabValue}
                            onChange={(_event: React.ChangeEvent<unknown>, newValue: number) => setTabValue(newValue)}
                        >
                            <Tab
                                ref={refSynopsisTab}
                                data-tour="prep-synopsis-tab"
                                label="Synopsis"
                                id="synopsis-tab"
                            />
                            <VTooltip
                                title={
                                    isBranchedStory
                                        ? "View the branched story structure and decision points"
                                        : "You can click on this tab to view the story and questions"
                                }
                                placement="bottom-start"
                                open={!tourState.openTour && storyTooltipOpen}
                            >
                                <Tab ref={refStoryTab} data-tour="prep-story-tab" label="Story" id="story-tab" />
                            </VTooltip>
                            <Tab
                                ref={refResourcesTab}
                                data-tour="prep-learn-tab"
                                label="Learning Resources"
                                id="bonus-tab"
                            />
                        </Tabs>
                    </Box>
                    <TabPanel padding={0} index={0} value={tabValue}>
                        <Box pt={6} px={6}>
                            <StorySynopsis story={item?.MappedStory} week={week} />
                        </Box>
                    </TabPanel>
                    <TabPanel padding={0} index={1} value={tabValue}>
                        {!isEditorLoaded && (
                            <Box display="flex" justifyContent="center" p={isBranchedStory ? 4 : 0}>
                                <Spinner />
                            </Box>
                        )}
                        {isBranchedStory ? (
                            <>
                                <Box
                                    p={3}
                                    style={isEditorLoaded ? { visibility: "visible" } : { visibility: "hidden" }}
                                >
                                    <Container maxWidth={"xl"}>
                                        {branchedLoading && <Spinner />}
                                        {branchedError && (
                                            <Typography color="error">
                                                Unable to load branched story structure. Please try refreshing the page.
                                            </Typography>
                                        )}
                                        {!branchedLoading && !branchedError && (
                                            <>
                                                {branchedStructure?.allParts &&
                                                branchedStructure.allParts.length > 0 ? (
                                                    <BranchedStoryAnnotationView
                                                        parts={branchedStructure.allParts}
                                                        facilitatorParts={facilitatorStoryParts || []}
                                                        content={content}
                                                        setContent={handleEditorChange}
                                                        setIsEdited={setIsEdited}
                                                        setIsEditorLoaded={setIsEditorLoaded}
                                                        isEditorLoaded={isEditorLoaded}
                                                        isQuestionsOpen={isQuestionsOpen}
                                                        setIsQuestionsOpen={setIsQuestionsOpen}
                                                        showQuestionsRef={refShowQuestions}
                                                        hideQuestionsRef={refHideQuestions}
                                                        tooltipOpen={tooltipOpen}
                                                        tourStateOpen={tourState.openTour}
                                                    />
                                                ) : (
                                                    <Typography>No story structure available</Typography>
                                                )}
                                            </>
                                        )}
                                    </Container>
                                </Box>
                            </>
                        ) : (
                            /* Linear Story View */
                            <>
                                {facilitatorStoryParts?.map((part, index) => {
                                    if (part.html) {
                                        return (
                                            <StoryPartAnnotation
                                                key={part.id}
                                                partNumber={index + 1}
                                                totalParts={facilitatorStoryParts.length}
                                                isExpanded={accordionOpen.get(part.id) ?? true}
                                                onToggleExpand={(expanded) =>
                                                    setAccordionOpen(new Map(accordionOpen.set(part.id, expanded)))
                                                }
                                                isQuestionsOpen={!!isQuestionsOpen.get(part.id)}
                                                onToggleQuestions={(open) =>
                                                    setIsQuestionsOpen(new Map(isQuestionsOpen.set(part.id, open)))
                                                }
                                                initialContent={part.html}
                                                mappedStoryPartId={part.mappedStoryPartId}
                                                content={content}
                                                setContent={handleEditorChange}
                                                setIsEdited={setIsEdited}
                                                setIsEditorLoaded={setIsEditorLoaded}
                                                isEditorLoaded={isEditorLoaded}
                                                mappedStoryPart={part.MappedStoryPart}
                                                mappedStoryId={item?.MappedStory?.id}
                                                imageUrl={part.MappedStoryPart?.imageUrl ?? undefined}
                                                isLoadingImage={!!loadingImagePreviews?.[index]}
                                                onImageClick={handleModalImage}
                                                onLoadImage={() => handleLoadingImagePreviews(index, false)}
                                                isMobile={isMobile}
                                                onBackToTop={() => {
                                                    setAccordionOpen(new Map(accordionOpen.set(part.id, true)));
                                                    partRefs.current[index]?.scrollIntoView({ behavior: "smooth" });
                                                }}
                                                accordionRef={(el: HTMLDivElement | null) => {
                                                    if (el !== null && !partRefs.current.includes(el)) {
                                                        partRefs.current.push(el);
                                                    }
                                                }}
                                                showQuestionsRef={index === 0 ? refShowQuestions : undefined}
                                                hideQuestionsRef={index === 0 ? refHideQuestions : undefined}
                                                isFirstPart={index === 0}
                                                tooltipOpen={tooltipOpen}
                                                tourStateOpen={tourState.openTour}
                                            />
                                        );
                                    } else {
                                        return null;
                                    }
                                })}
                                <Modal
                                    className={classes.modal}
                                    open={openModalImagePreview}
                                    onClose={handleModalImagePreviewClose}
                                    closeAfterTransition
                                    slots={{ backdrop: Backdrop }}
                                    slotProps={{
                                        backdrop: {
                                            timeout: 500,
                                            sx: { backgroundColor: "rgba(0, 0, 0, 0.85)" },
                                        },
                                    }}
                                >
                                    <Fade in={openModalImagePreview} timeout={500}>
                                        <Box
                                            sx={{
                                                outline: "none",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                            }}
                                        >
                                            <img
                                                src={modalImage}
                                                alt="Preview"
                                                style={{
                                                    maxHeight: "90vh",
                                                    maxWidth: "90vw",
                                                    objectFit: "contain",
                                                    borderRadius: "8px",
                                                }}
                                            />
                                        </Box>
                                    </Fade>
                                </Modal>
                            </>
                        )}
                        {isEditorLoaded && (
                            <Box m={isBranchedStory ? 2 : 0}>
                                <ActionFooter
                                    onCancel={handleCancel}
                                    onSave={handleSaveAnnotation}
                                    isSaving={saving}
                                    dataTour="prepare-story-save"
                                />
                            </Box>
                        )}
                    </TabPanel>
                    <TabPanel padding={0} index={2} value={tabValue}>
                        <FileViewer
                            url={
                                driveFolderId
                                    ? `https://drive.google.com/embeddedfolderview?id=${driveFolderId}#list`
                                    : undefined
                            }
                            title={item?.MappedStory?.Story?.storyName}
                            noneMessage="No bonus content with this story"
                        />
                    </TabPanel>
                </Container>
            </Box>
            <Dialog open={confirmCancel} onClose={() => setConfirmCancel(false)}>
                <DialogTitle>Cancel Story Annotations?</DialogTitle>
                <DialogContent>
                    <Typography>
                        You have annotated the story. Are you sure you want to cancel and discard your annotations?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        variant="contained"
                        size="large"
                        color="secondary"
                        onClick={() => setConfirmCancel(false)}
                        style={{ fontWeight: "bold" }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        size="large"
                        color="primary"
                        onClick={() => push(programmeUrl)}
                        style={{ fontWeight: "bold" }}
                    >
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};
